import styled from "@emotion/styled";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { Typography } from "@avenue-8/sidekick-ui";
import { ContactsExtractionConsent } from "./ContactsExtractionConsent";
import { AddContactMenu } from "./AddContactMenu";

const Container = styled.div<{ $horizontal?: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.$horizontal ? "row" : "column")};
  align-items: ${(p) => (p.$horizontal ? "stretch" : "center")};
  gap: 1rem;
  padding-top: ${(p) => (p.$horizontal ? "0" : "1rem")};

  ${(p) =>
    p.$horizontal &&
    `
    ${p.theme.media.tabletDown} {
      flex-direction: column;
      align-items: center;
      gap: 1rem;
    }
  `}
`;

const Card = styled.div<{ $horizontal?: boolean }>`
  width: 100%;
  padding: 2rem 1.5rem;
  border: 1px solid ${(p) => p.theme.colors.border2};
  border-radius: 1rem;
  background-color: ${(p) => p.theme.colors.background};

  ${(p) =>
    p.$horizontal
      ? `
    flex: 1;
    min-width: 0;
  `
      : `max-width: 520px;`}
`;

const ManualAddSection = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
`;

const CardTitle = styled(Typography)`
  line-height: 1.2;
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  margin-bottom: 0.5rem;
  border-radius: 50%;
  background-color: ${(p) => p.theme.colors.background5};
  color: ${(p) => p.theme.text.colors.brand};
`;

// A CSS-only "OR" divider (rather than MUI's Divider with a JS `orientation`
// prop) so it can never fall out of sync with $horizontal's own CSS-driven
// collapse to a column at tabletDown - a JS-computed orientation stays
// "vertical" through that collapse and renders as a zero-height line.
const OrDivider = styled.div<{ $horizontal?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  max-width: 520px;
  color: ${(p) => p.theme.text.colors.secondary};

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background-color: ${(p) => p.theme.colors.border2};
  }

  ${(p) =>
    p.$horizontal &&
    `
    flex-direction: column;
    align-self: stretch;
    width: auto;
    max-width: none;

    &::before, &::after {
      width: 1px;
      height: auto;
    }

    ${p.theme.media.tabletDown} {
      flex-direction: row;
      align-self: auto;
      width: 100%;

      &::before, &::after {
        width: auto;
        height: 1px;
      }
    }
  `}
`;

type ContactsEmptyStateProps = {
  onAddContact: () => void;
  onImportFromCsv: () => void;
  isGoogleConnected: boolean;
  hasGoogleAccess: boolean;
  // TEMPORARY: restricted to Radar users until "add contact" opens up to all
  // CRM-enabled users, matching the same gate on the page header's button.
  hasRadarModule: boolean;
  // "vertical" (default) stacks with a horizontal "OR" divider, matching the
  // Contacts page. "horizontal" sits the two cards side by side with a
  // vertical divider between them, for wider/shorter panels.
  orientation?: "vertical" | "horizontal";
};

export const ContactsEmptyState = ({
  onAddContact,
  onImportFromCsv,
  isGoogleConnected,
  hasGoogleAccess,
  hasRadarModule,
  orientation = "vertical",
}: ContactsEmptyStateProps) => {
  const isHorizontal = orientation === "horizontal";

  return (
    <Container $horizontal={isHorizontal}>
      {hasRadarModule && (
        <>
          <ManualAddSection $horizontal={isHorizontal}>
            <IconWrap>
              <PersonAddAltIcon fontSize="medium" />
            </IconWrap>
            <CardTitle variant="subtitle1">
              Add your first contact manually
            </CardTitle>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enter one contact&apos;s details, or import a list from a CSV
              file.
            </Typography>
            <AddContactMenu
              variant="regular"
              onAddManually={onAddContact}
              onImportFromCsv={onImportFromCsv}
            />
          </ManualAddSection>
          {!hasGoogleAccess && (
            <OrDivider $horizontal={isHorizontal}>
              <Typography variant="body2">OR</Typography>
            </OrDivider>
          )}
        </>
      )}
      {!hasGoogleAccess && (
        <Card $horizontal={isHorizontal}>
          <ContactsExtractionConsent
            size="medium"
            isConnected={isGoogleConnected}
          />
        </Card>
      )}
      {hasGoogleAccess && !hasRadarModule && (
        <ManualAddSection $horizontal={isHorizontal}>
          <Typography variant="body2" color="text.secondary">
            We didn&apos;t find any contacts in your inbox yet.
          </Typography>
        </ManualAddSection>
      )}
    </Container>
  );
};
